import { useMemo, useState } from 'react'
import { useSelectedShop } from '../ShopContext'
import { useOrders, deriveOrderState, type Order, type OrderState } from '../useOrders'
import { OrderDetailScreen } from './OrderDetailScreen'

// Orders inbox — list view for the currently-selected shop with filter
// chips (All / Needs Shipping / Shipped / Refunded) and per-filter counts.
// Filter is client-side (no new API): server returns the last N orders and
// we split them by deriveOrderState. Counts always reflect the full server
// list, not the filtered subset — merchant needs to see "3 pending" at a
// glance regardless of which chip is active.

type Filter = 'all' | OrderState

// Fixed chip order and labels. `returned` is intentionally omitted for
// v0.1 — the return workflow is a Wave 4.5 concern; if merchants have
// returns they still show up under "All" with the red pill.
const FILTER_CHIPS: Array<{ key: Filter; label: string }> = [
  { key: 'all',            label: 'All' },
  { key: 'needs-shipping', label: 'Needs shipping' },
  { key: 'shipped',        label: 'Shipped' },
  { key: 'refunded',       label: 'Refunded' },
]

export function OrdersScreen() {
  const shop = useSelectedShop()
  const { state, refresh } = useOrders(shop?.handle ?? null)

  // Local nav — no router, just a single "selected order" slot. Tapping a
  // card sets it; the back button clears. Persists nothing across app
  // relaunches (order URLs would require a real router; deferred).
  const [selected, setSelected] = useState<Order | null>(null)
  const [filter,   setFilter]   = useState<Filter>('all')

  // No shop selected (merchant has zero shops, or shop list is still loading
  // when App.tsx let us render). Render a friendly empty state instead of
  // an "Orders" header with no content.
  if (!shop) {
    return (
      <div style={s.wrap}>
        <div style={s.eyebrow}>Orders</div>
        <div style={s.placeholder}>
          No shop selected yet. Create your first shop at
          {' '}<strong>headlo.com</strong>{' '}
          — new orders appear here as soon as they land.
        </div>
      </div>
    )
  }

  // Precompute the full list's counts + filter split. Memoized so we don't
  // re-derive state per order on every re-render (matters for filter
  // toggle at N=200 orders).
  const allOrders = state.status === 'ready' ? state.orders : []
  const { counts, filtered } = useMemo(() => {
    const counts: Record<Filter, number> = { 'all': allOrders.length, 'needs-shipping': 0, 'shipped': 0, 'refunded': 0, 'returned': 0 }
    for (const o of allOrders) counts[deriveOrderState(o)]++
    const filtered = filter === 'all'
      ? allOrders
      : allOrders.filter(o => deriveOrderState(o) === filter)
    return { counts, filtered }
  }, [allOrders, filter])

  if (selected) {
    return (
      <OrderDetailScreen
        order={selected}
        onBack={() => setSelected(null)}
        onChanged={refresh}
      />
    )
  }

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div style={s.headerText}>
          <div style={s.eyebrow}>Orders</div>
          <div style={s.shopNameRow}>
            <h1 style={s.shopName}>{shop?.brand_name || shop?.handle || 'Orders'}</h1>
            {shop?.has_shopify && <span style={s.shopifyBadge}>Shopify</span>}
          </div>
        </div>
        <button
          style={s.refreshBtn}
          onClick={refresh}
          disabled={state.status === 'loading' || (state.status === 'ready' && state.refreshing)}
          aria-label="Refresh"
          title="Refresh"
        >
          {state.status === 'loading' || (state.status === 'ready' && state.refreshing) ? '…' : '↻'}
        </button>
      </div>

      {/* Filter chips — always rendered (even during load) so the layout
          doesn't jump. Counts of 0 aren't hidden; empty chips still show
          as tappable "Shipped (0)" so the merchant knows the filter is
          available. Horizontal scroll on narrow screens. */}
      {state.status === 'ready' && (
        <div style={s.chipRow} className="headlo-scroll-hide">
          {FILTER_CHIPS.map(chip => {
            const count  = counts[chip.key]
            const active = filter === chip.key
            return (
              <button
                key={chip.key}
                type="button"
                onClick={() => setFilter(chip.key)}
                style={{ ...s.chip, ...(active ? s.chipActive : {}) }}
              >
                {chip.label} <span style={s.chipCount}>{count}</span>
              </button>
            )
          })}
        </div>
      )}

      {state.status === 'loading' && (
        <div style={s.placeholder}>Loading orders…</div>
      )}

      {state.status === 'error' && (
        <div style={s.errorCard}>
          <div style={s.errorTitle}>Couldn't load orders</div>
          <div style={s.errorMsg}>{state.message}</div>
          <button style={s.retryBtn} onClick={refresh}>Try again</button>
        </div>
      )}

      {state.status === 'ready' && allOrders.length === 0 && (
        <div style={s.placeholder}>
          No orders yet. When a buyer places an order they'll appear here.
        </div>
      )}

      {state.status === 'ready' && allOrders.length > 0 && filtered.length === 0 && (
        <div style={s.placeholder}>
          {emptyMessageFor(filter)}
        </div>
      )}

      {state.status === 'ready' && filtered.length > 0 && (
        <div style={s.list}>
          {filtered.map(order => (
            <OrderRow key={order.shop_order_id} order={order} onOpen={() => setSelected(order)} />
          ))}
        </div>
      )}
    </div>
  )
}

// Filter-specific empty state copy. Keeps the tone matched to what the
// merchant expected to see — "no shipped orders" is different news from
// "no orders at all."
function emptyMessageFor(filter: Filter): string {
  switch (filter) {
    case 'needs-shipping': return 'Nothing waiting to ship. Nice.'
    case 'shipped':        return 'No shipped orders in this batch.'
    case 'refunded':       return 'No refunds — good sign.'
    default:               return 'No orders in this filter.'
  }
}

// ── OrderRow ────────────────────────────────────────────────────────────────

function OrderRow({ order, onOpen }: { order: Order; onOpen: () => void }) {
  const orderState  = deriveOrderState(order)
  const buyerName   = order.shipping_address?.name?.trim() || order.buyer_email
  const summary     = itemsSummary(order)
  const totalDollars = ((order.amount_cents ?? 0) / 100).toFixed(2)
  const ago         = timeAgo(order.created_at)

  return (
    <button type="button" style={s.rowBtn} onClick={onOpen}>
      <div style={s.rowTop}>
        <div style={s.buyerBlock}>
          <div style={s.buyer}>{buyerName}</div>
          {order.shipping_address?.name && (
            <div style={s.buyerEmail}>{order.buyer_email}</div>
          )}
        </div>
        <div style={s.total}>${totalDollars}</div>
      </div>
      <div style={s.summary}>{summary}</div>
      <div style={s.rowBottom}>
        <StatusPill state={orderState} order={order} />
        <span style={s.ago}>{ago}</span>
      </div>
    </button>
  )
}

function StatusPill({ state, order }: { state: OrderState; order: Order }) {
  const cfg = STATUS_CONFIG[state]
  const suffix =
    state === 'shipped' && order.tracking_number
      ? ` · ${order.tracking_number}`
      : ''
  return (
    <span style={{ ...s.pill, background: cfg.bg, color: cfg.color }}>
      <span style={s.pillDot}>●</span>
      {cfg.label}{suffix}
    </span>
  )
}

const STATUS_CONFIG: Record<OrderState, { label: string; bg: string; color: string }> = {
  'needs-shipping': { label: 'Needs shipping', bg: '#3a2f1e', color: '#e5b880' },
  'shipped':        { label: 'Shipped',        bg: '#1e3a24', color: '#a5d8b0' },
  'returned':       { label: 'Return',         bg: '#3a1e1e', color: '#f5b7b7' },
  'refunded':       { label: 'Refunded',       bg: '#2a2a2a', color: '#aaaaaa' },
}

// ── Helpers ────────────────────────────────────────────────────────────────

function itemsSummary(order: Order): string {
  if (!order.items || order.items.length === 0) {
    // Legacy orders (pre-items column) — fall back to product_type + sku.
    return `${order.product_type || 'Item'}${order.sku ? ` · ${order.sku}` : ''}`
  }
  const first = order.items[0]!
  const firstLine = `${first.product_name}${first.quantity > 1 ? ` × ${first.quantity}` : ''}`
  const extra = order.items.length - 1
  return extra > 0 ? `${firstLine} + ${extra} more` : firstLine
}

function timeAgo(iso: string): string {
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return ''
  const seconds = Math.floor((Date.now() - then) / 1000)
  if (seconds < 60)      return 'just now'
  if (seconds < 3600)    return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400)   return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 604800)  return `${Math.floor(seconds / 86400)}d ago`
  return new Date(then).toLocaleDateString()
}

// ── Styles ─────────────────────────────────────────────────────────────────

const s = {
  wrap: {
    // Extra bottom padding accounts for the bottom-tab bar sitting on top
    // of the scroll area; without it the last order card can be partially
    // occluded when the list ends near the bottom of the viewport.
    padding: '24px 20px calc(24px + env(safe-area-inset-bottom))',
  },
  chipRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
    overflowX: 'auto' as const,
    // Hide the horizontal scrollbar visually; users still scroll via swipe.
    WebkitOverflowScrolling: 'touch' as const,
    // Negative side margin lets chips scroll edge-to-edge; padding
    // restores the 20px gutter for the actual content.
    marginLeft: -20,
    marginRight: -20,
    paddingLeft: 20,
    paddingRight: 20,
    scrollbarWidth: 'none' as const,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '7px 12px',
    fontSize: 13,
    fontWeight: 600,
    background: '#ffffff',
    color: '#5a5a55',
    border: '1px solid #e5e5e0',
    borderRadius: 16,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  },
  chipActive: {
    background: '#1a1a18',
    color: '#faf9f6',
    borderColor: '#1a1a18',
  },
  chipCount: {
    fontSize: 11,
    fontWeight: 700,
    opacity: 0.7,
    fontVariantNumeric: 'tabular-nums' as const,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 20,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    color: '#8a8a80',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  shopNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  shopName: {
    fontSize: 28,
    fontWeight: 700,
    marginTop: 0,
    marginBottom: 0,
    color: '#1a1a18',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    minWidth: 0,
  },
  shopifyBadge: {
    padding: '2px 8px',
    background: '#95bf47',
    color: '#ffffff',
    borderRadius: 4,
    fontSize: 10,
    fontFamily: 'inherit',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.3,
    flexShrink: 0,
  },
  refreshBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    background: '#ffffff',
    color: '#1a1a18',
    border: '1px solid #e5e5e0',
    cursor: 'pointer',
    fontSize: 18,
    fontFamily: 'inherit',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  placeholder: {
    padding: 20,
    background: '#f0efe9',
    borderRadius: 12,
    fontSize: 14,
    color: '#5a5a55',
    lineHeight: 1.55,
    textAlign: 'center' as const,
  },
  errorCard: {
    padding: 20,
    background: '#3a1e1e',
    color: '#f5b7b7',
    borderRadius: 12,
  },
  errorTitle: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 6,
  },
  errorMsg: {
    fontSize: 12,
    marginBottom: 12,
    fontFamily: '"SF Mono", Consolas, monospace',
    opacity: 0.9,
  },
  retryBtn: {
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    background: '#f5b7b7',
    color: '#3a1e1e',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  list: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 10,
  },
  row: {
    padding: '14px 16px',
    background: '#ffffff',
    border: '1px solid #e5e5e0',
    borderRadius: 12,
  },
  rowBtn: {
    // Same visual as `row`, but as a <button> so the native tap feedback
    // + accessibility affordances come for free.
    display: 'block',
    width: '100%',
    textAlign: 'left' as const,
    padding: '14px 16px',
    background: '#ffffff',
    border: '1px solid #e5e5e0',
    borderRadius: 12,
    cursor: 'pointer',
    fontFamily: 'inherit',
    color: 'inherit',
  },
  rowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 6,
  },
  buyerBlock: {
    flex: 1,
    minWidth: 0,
  },
  buyer: {
    fontSize: 15,
    fontWeight: 600,
    color: '#1a1a18',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  buyerEmail: {
    fontSize: 12,
    color: '#8a8a80',
    fontFamily: '"SF Mono", Consolas, monospace',
    marginTop: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  total: {
    fontSize: 16,
    fontWeight: 700,
    color: '#1a1a18',
    fontVariantNumeric: 'tabular-nums' as const,
    flexShrink: 0,
  },
  summary: {
    fontSize: 13,
    color: '#5a5a55',
    marginBottom: 10,
    lineHeight: 1.4,
  },
  rowBottom: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    fontSize: 12,
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '3px 9px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    maxWidth: '65%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  pillDot: {
    fontSize: 8,
    lineHeight: 1,
  },
  ago: {
    fontSize: 12,
    color: '#8a8a80',
    fontVariantNumeric: 'tabular-nums' as const,
    flexShrink: 0,
  },
} as const
